import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, UntypedFormArray } from '@angular/forms';
import { SelectInputOption } from '@vendasta/galaxy/input';
import { Observable } from 'rxjs';
import { KeywordInfo } from '@vendasta/listing-products';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { KeywordTrackingService } from '../keyword-tracking/keyword-tracking.service';

export interface KeywordDialogResult {
  originalKeywords: string[];
  newKeywords: string[];
}

@Component({
  selector: 'local-seo-keyword-dialog',
  templateUrl: './local-seo-keyword-dialog.component.html',
  styleUrls: ['./local-seo-keyword-dialog.component.scss'],
  standalone: false,
})
export class LocalSeoKeywordDialogComponent {
  keywordLimit: number;
  form: UntypedFormArray;
  options: SelectInputOption[];
  suggestedKeywordList: KeywordInfo[];
  displayedColumns: string[] = ['keyword', 'competition', 'search_volume', 'add_button'];
  isLbFreeEdition$: Observable<boolean>;
  canEditKeywords: boolean;

  originalKeywords: string[] = [];

  public suggestedKeywordsErrorMessage: string | null = null;
  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      keywords: string[];
      keywordLimit: number;
      suggestedKeywords: KeywordInfo[];
      canEditKeywords: boolean;
    },
    public dialogRef: MatDialogRef<LocalSeoKeywordDialogComponent>,
    private fb: FormBuilder,
    private keywordTrackingService: KeywordTrackingService,
    private posthogService: ProductAnalyticsService,
  ) {
    this.originalKeywords = this.data.keywords;
    this.canEditKeywords = data.canEditKeywords;
    this.isLbFreeEdition$ = this.keywordTrackingService.isLBFreeEdition$;
    this.suggestedKeywordList = data.suggestedKeywords;
    this.suggestedKeywordsErrorMessage = this.keywordTrackingService.suggestedKeywordsErrorMessage;
    this.keywordLimit = data.keywordLimit;
    this.form = new UntypedFormArray([]);
    this.form.controls.push(this.fb.control(''));
  }

  currentKeywordUsage() {
    const inputsWithText = this.form.controls.filter((control) => control.value.length > 0).length;
    return this.data.keywords.length + inputsWithText;
  }

  closeModal(save: boolean): void {
    // forms-va-input-repeated returns [''] by default. We need to filter this out.
    const keywordsAdded = this.form.controls.map((control) => control.value).filter((value) => value !== '');
    if (!keywordsAdded.length) {
      this.dialogRef.close(undefined);
    }

    const result: KeywordDialogResult = {
      originalKeywords: this.originalKeywords,
      newKeywords: keywordsAdded,
    };

    this.raisePostHogEvent(keywordsAdded);

    this.dialogRef.close(save ? result : undefined);
  }

  raisePostHogEvent(keywordsAdded: any[]): void {
    const suggestedKeywordsAdded = this.data.suggestedKeywords.filter((keyword) =>
      keywordsAdded.includes(keyword.keyword),
    );

    for (const suggestedKeywordsAddedElement of suggestedKeywordsAdded) {
      this.posthogService.trackEvent('suggested-keywords-added', 'suggest-keyword', 'save', 1, {
        keyword: suggestedKeywordsAddedElement.keyword,
      });
    }
  }

  addSuggestedKeyword(element: KeywordInfo): void {
    this.form.controls.push(this.fb.control(element.keyword));
    this.form.controls = this.form.controls.filter((control) => control.value !== '');
    this.suggestedKeywordList = this.suggestedKeywordList.filter((keyword) => keyword.keyword !== element.keyword);
  }

  removeClicked(keyword: string): void {
    const removedKeyword = this.data.suggestedKeywords.find((keywordInfo) => keywordInfo.keyword === keyword);
    if (!removedKeyword) {
      return;
    }
    this.suggestedKeywordList.push(removedKeyword);
    this.suggestedKeywordList = [].concat(this.suggestedKeywordList);
  }
}
